import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { useCasePartnerOpenActions } from '@/components/contexts/CasePartnerContext';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { apiErrorMessage } from '@/lib/api-client';
import type { DocumentSelection } from '@/types';

interface OpenInCasePartnerDocument {
  guid?: string;
  id?: string;
  displayName?: string;
  fileName?: string;
  token_count?: number;
  attachments?: OpenInCasePartnerDocument[];
}

type OpenInCasePartnerTarget =
  | string
  | OpenInCasePartnerDocument
  | Array<string | OpenInCasePartnerDocument>;

interface OpenInCasePartnerOptions {
  draft?: string;
}

const getTargetGuid = (target: string | OpenInCasePartnerDocument) => (
  typeof target === 'string' ? target : target.guid || target.id || ''
).trim();

const createOptimisticSelection = (document: OpenInCasePartnerDocument): DocumentSelection | null => {
  const guid = getTargetGuid(document);
  if (!guid) return null;

  const displayName = document.displayName || document.fileName || 'Document';
  const fileName = document.fileName || document.displayName || 'Document';

  return {
    guid,
    include_parent: true,
    attachments: (document.attachments || [])
      .map((attachment) => {
        const attachmentGuid = getTargetGuid(attachment);
        if (!attachmentGuid) return null;

        return {
          guid: attachmentGuid,
          included: true,
          filename: attachment.fileName || attachment.displayName || 'Attachment',
          token_count: attachment.token_count || 0,
          hasAttachments: Array.isArray(attachment.attachments) && attachment.attachments.length > 0,
        };
      })
      .filter((attachment): attachment is NonNullable<typeof attachment> => attachment !== null),
    displayName,
    fileName,
    token_count: document.token_count || 0,
  };
};

export function useOpenInCasePartner() {
  const { openDraftWithDocuments } = useCasePartnerOpenActions();
  const { showCasePartnerTab } = useSidePanel();

  return useCallback((
    matterGuid: string | undefined,
    targets: OpenInCasePartnerTarget | undefined,
    options?: OpenInCasePartnerOptions
  ) => {
    if (!matterGuid || !targets) return;

    const nextTargets = Array.isArray(targets) ? targets : [targets];
    const nextDocumentGuids = nextTargets
      .map(getTargetGuid)
      .filter((guid) => guid.length > 0);

    if (nextDocumentGuids.length === 0) return;

    const optimisticSelections = nextTargets
      .filter((target): target is OpenInCasePartnerDocument => typeof target !== 'string')
      .map(createOptimisticSelection)
      .filter((selection): selection is DocumentSelection => selection !== null);

    openDraftWithDocuments(matterGuid, nextDocumentGuids, optimisticSelections, options?.draft);
    showCasePartnerTab({ activate: true, open: true });
  }, [
    openDraftWithDocuments,
    showCasePartnerTab,
  ]);
}

export function useOpenChatInCasePartner() {
  const { openExistingChat } = useCasePartnerOpenActions();
  const { showCasePartnerTab } = useSidePanel();

  return useCallback((chatGuid: string) => {
    const nextChatGuid = chatGuid.trim();
    if (!nextChatGuid) return;

    showCasePartnerTab({ activate: true, open: true });
    void openExistingChat(nextChatGuid).catch((error) => {
      toast.error(apiErrorMessage(error) || 'Could not open the source chat');
    });
  }, [openExistingChat, showCasePartnerTab]);
}
